import React, { useCallback } from 'react';
import {
  V1AllergiesCard,
  V1DiagnosesCard,
  V1MedicationsCard,
} from '@features/medications-workspace';
import { useSubjectLanguageVariants } from '@features/user';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';

export type UnifiedClinicalModule = 'medications' | 'diagnoses' | 'allergies';

const MODULE_TITLE: Record<UnifiedClinicalModule, string> = {
  medications: 'Medications',
  diagnoses: 'Diagnoses',
  allergies: 'Allergies',
};

interface UnifiedClinicalEditModalProps {
  open: boolean;
  onClose: () => void;
  patientId: string;
  module: UnifiedClinicalModule;
  /** Source identifier for analytics */
  source: string;
}

/**
 * The context-bar edit modal behind feature_unified_medications_card: hosts
 * the same V1 clinical-card container the patient page renders, so the bar
 * and the chart share one component per module (full CRUD, unified statuses,
 * Manage-in-DoseSpot, consent section). The containers own the fine-grained
 * CRUD analytics (unifiedMedications / clinicalCards families);
 * `trackImpression={false}` because this surface is already counted by the
 * host's editModalOpened, and this dialog closes the pair with
 * editModalClosed — same contract as the legacy edit modals.
 */
export const UnifiedClinicalEditModal: React.FC<UnifiedClinicalEditModalProps> = ({
  open,
  onClose,
  patientId,
  module,
  source,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { singularCap } = useSubjectLanguageVariants();
  const { patientContextBar: contextBarTracking } = useEventTracking();

  const handleClose = useCallback(() => {
    contextBarTracking.editModalClosed({
      patient_id: patientId,
      data_type: module,
      had_changes: false,
      source,
    });
    onClose();
  }, [contextBarTracking, module, onClose, patientId, source]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={fullScreen}
      fullWidth
      maxWidth="lg"
      slotProps={{
        paper: {
          // The accessible name must live on the Paper — the element that
          // carries role="dialog". Dialog forwards only aria-labelledby/
          // aria-describedby there; a top-level aria-label lands on the
          // Modal root, outside the name computation.
          'aria-label': `Edit ${singularCap} ${MODULE_TITLE[module]}`,
          sx: {
            height: fullScreen ? '100%' : '80vh',
            maxHeight: fullScreen ? '100%' : '80vh',
            bgcolor: 'background.paper',
            boxShadow: 'none',
            border: '1px solid',
            borderColor: 'divider',
          },
        },
      }}
    >
      {/* No DialogTitle: the embedded card renders the ONE header row
          (title + filter + Add + close X) so the modal never double-stacks
          chrome; slotProps.paper's aria-label carries the accessible name. */}
      <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          {module === 'medications' && (
            <V1MedicationsCard
              patientId={patientId}
              trackImpression={false}
              variant="embedded"
              onClose={handleClose}
            />
          )}
          {module === 'diagnoses' && (
            <V1DiagnosesCard
              patientId={patientId}
              trackImpression={false}
              variant="embedded"
              onClose={handleClose}
            />
          )}
          {module === 'allergies' && (
            <V1AllergiesCard
              patientId={patientId}
              trackImpression={false}
              variant="embedded"
              onClose={handleClose}
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          onClick={handleClose}
          variant="contained"
          sx={{
            borderRadius: '999px',
            textTransform: 'none',
            fontWeight: 500,
            px: 3,
            bgcolor: 'primary.main',
            '&:hover': { bgcolor: 'primary.dark' },
          }}
        >
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};
